import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Format montant en français
const formatMontant = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value) + ' €';
};

// Format montant avec signe
const formatMontantAvecSigne = (value) => {
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(value));
  
  if (value > 0) return `+ ${formatted} €`;
  if (value < 0) return `- ${formatted} €`;
  return `${formatted} €`;
};

// Format date en français
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Format date et heure
const formatDateTime = () => {
  return new Date().toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Ajouter le filigrane sur toutes les pages
const addWatermark = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Texte du filigrane en diagonale
    doc.saveGraphicsState();
    
    // Couleur grise très claire
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(50);
    doc.setFont('helvetica', 'bold');
    
    // Rotation et positionnement central
    const text = 'VERSION GRATUITE';
    const textWidth = doc.getTextWidth(text);
    
    // Dessiner le filigrane en diagonale au centre
    doc.text(text, pageWidth / 2 - textWidth / 3, pageHeight / 2 + 20, {
      angle: 45,
      align: 'center'
    });
    
    // Deuxième ligne
    doc.setFontSize(20);
    doc.text('Passez à Solo pour supprimer ce filigrane', pageWidth / 2, pageHeight / 2 + 50, {
      angle: 45,
      align: 'center'
    });
    
    doc.restoreGraphicsState();
  }
};

/**
 * Export PDF d'un mois
 * @param {string} mois - Nom du mois
 * @param {number} annee - Année
 * @param {Array} recettes - Liste des recettes
 * @param {Array} depenses - Liste des dépenses
 * @param {boolean} hasWatermark - Ajouter un filigrane (plan gratuit)
 */
export const exportPDFMois = (mois, annee, recettes, depenses, hasWatermark = false) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Calculer les totaux
  const totalRecettes = recettes.reduce((sum, r) => sum + r.montant, 0);
  const totalDepenses = depenses.reduce((sum, d) => sum + d.montant, 0);
  const solde = totalRecettes - totalDepenses;
  
  // --- EN-TÊTE ---
  doc.setFillColor(37, 99, 235); // Bleu
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`5minCompta - ${mois} ${annee}`, pageWidth / 2, 15, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('5minCompta - Livre chronologique des recettes', pageWidth / 2, 23, { align: 'center' });
  
  doc.setFontSize(8);
  doc.text(`Document généré le ${formatDateTime()}`, pageWidth / 2, 30, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  let yPos = 45;
  
  // --- SECTION RECETTES ---
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(16, 185, 129); // Vert
  doc.text('LIVRE CHRONOLOGIQUE DES RECETTES', 14, yPos);
  
  doc.setTextColor(0, 0, 0);
  yPos += 5;
  
  if (recettes.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Réf.', 'Client', 'Nature', 'Montant', 'Mode']],
      body: recettes.map(r => [
        formatDate(r.date),
        r.reference,
        r.client,
        r.nature,
        formatMontant(r.montant),
        r.mode_encaissement
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [240, 253, 244]
      },
      columnStyles: {
        4: { halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 }
    });
    
    yPos = doc.lastAutoTable.finalY + 3;
  } else {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Aucune recette ce mois', 14, yPos + 5);
    yPos += 10;
  }
  
  // Total recettes
  doc.setFillColor(209, 250, 229);
  doc.rect(14, yPos, pageWidth - 28, 8, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(6, 95, 70);
  doc.text('TOTAL RECETTES :', 16, yPos + 5.5);
  doc.text(formatMontant(totalRecettes), pageWidth - 16, yPos + 5.5, { align: 'right' });
  
  yPos += 15;
  
  // --- SECTION DÉPENSES ---
  doc.setTextColor(239, 68, 68); // Rouge
  doc.setFontSize(12);
  doc.text('REGISTRE DES ACHATS ET AUTRES DÉPENSES', 14, yPos);
  
  doc.setTextColor(0, 0, 0);
  yPos += 5;
  
  if (depenses.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Réf.', 'Fournisseur', 'Nature', 'Montant', 'Mode']],
      body: depenses.map(d => [
        formatDate(d.date),
        d.reference,
        d.fournisseur,
        d.nature,
        formatMontant(d.montant),
        d.mode_paiement
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [239, 68, 68],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [254, 242, 242]
      },
      columnStyles: {
        4: { halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 }
    });
    
    yPos = doc.lastAutoTable.finalY + 3;
  } else {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Aucune dépense ce mois', 14, yPos + 5);
    yPos += 10;
  }
  
  // Total dépenses
  doc.setFillColor(254, 226, 226);
  doc.rect(14, yPos, pageWidth - 28, 8, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(153, 27, 27);
  doc.text('TOTAL DÉPENSES :', 16, yPos + 5.5);
  doc.text(formatMontant(totalDepenses), pageWidth - 16, yPos + 5.5, { align: 'right' });
  
  yPos += 18;
  
  // --- SOLDE DU MOIS ---
  const soldeColor = solde >= 0 ? [16, 185, 129] : [239, 68, 68];
  const soldeBgColor = solde >= 0 ? [209, 250, 229] : [254, 226, 226];
  
  doc.setFillColor(...soldeBgColor);
  doc.rect(14, yPos, pageWidth - 28, 12, 'F');
  doc.setDrawColor(...soldeColor);
  doc.setLineWidth(0.5);
  doc.rect(14, yPos, pageWidth - 28, 12, 'S');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...soldeColor);
  doc.text('SOLDE DU MOIS :', 16, yPos + 8);
  doc.text(formatMontantAvecSigne(solde), pageWidth - 16, yPos + 8, { align: 'right' });
  
  // --- PIED DE PAGE ---
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'Document conforme à l\'article 99 du Code Général des Impôts',
    pageWidth / 2,
    pageHeight - 15,
    { align: 'center' }
  );
  doc.text(
    `Page 1/1 - Généré le ${formatDateTime()}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );
  
  // Ajouter le filigrane si plan gratuit
  if (hasWatermark) {
    addWatermark(doc);
  }
  
  // Télécharger le PDF
  const fileName = `5minCompta-${mois}-${annee}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

/**
 * Export PDF du récapitulatif annuel
 * @param {number} annee - Année
 * @param {Object} summary - Données du récapitulatif
 * @param {Object} stats - Statistiques
 * @param {boolean} hasWatermark - Ajouter un filigrane (plan gratuit)
 */
export const exportPDFRecapitulatif = (annee, summary, stats, hasWatermark = false) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // --- EN-TÊTE ---
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(`Récapitulatif Annuel ${annee}`, pageWidth / 2, 15, { align: 'center' });
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('5minCompta - Auto-entrepreneur', pageWidth / 2, 24, { align: 'center' });
  
  doc.setFontSize(8);
  doc.text(`Document généré le ${formatDateTime()}`, pageWidth / 2, 31, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  
  // --- RÉSUMÉ ---
  let yPos = 45;
  
  // Cartes KPI
  const cardWidth = 42;
  const cardHeight = 20;
  const startX = 14;
  const gap = 4;
  
  const kpis = [
    { label: 'Total Recettes', value: summary.total_recettes, color: [16, 185, 129] },
    { label: 'Total Dépenses', value: summary.total_depenses, color: [239, 68, 68] },
    { label: 'Solde Annuel', value: summary.solde, color: summary.solde >= 0 ? [16, 185, 129] : [239, 68, 68], withSign: true },
    { label: 'Trésorerie', value: summary.tresorerie, color: [37, 99, 235] }
  ];
  
  kpis.forEach((kpi, i) => {
    const x = startX + i * (cardWidth + gap);
    
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(x, yPos, cardWidth, cardHeight, 2, 2, 'F');
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(kpi.label, x + 3, yPos + 6);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...kpi.color);
    doc.text(
      kpi.withSign ? formatMontantAvecSigne(kpi.value) : formatMontant(kpi.value),
      x + 3,
      yPos + 14
    );
  });
  
  yPos += 30;
  
  // --- TABLEAU RÉCAPITULATIF ---
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Récapitulatif mensuel', 14, yPos);
  
  yPos += 5;
  
  // Calculer trésorerie cumulée
  let tresorerieCumul = 0;
  const tableData = summary.monthly_data.map(m => {
    const hasData = m.total_recettes > 0 || m.total_depenses > 0;
    tresorerieCumul += m.solde;
    return [
      m.mois_nom,
      hasData ? formatMontant(m.total_recettes) : '—',
      hasData ? formatMontant(m.total_depenses) : '—',
      hasData ? formatMontantAvecSigne(m.solde) : '—',
      formatMontant(tresorerieCumul)
    ];
  });
  
  autoTable(doc, {
    startY: yPos,
    head: [['Mois', 'Recettes', 'Dépenses', 'Solde', 'Trésorerie']],
    body: tableData,
    foot: [[
      `TOTAL ${annee}`,
      formatMontant(summary.total_recettes),
      formatMontant(summary.total_depenses),
      formatMontantAvecSigne(summary.solde),
      formatMontant(summary.tresorerie)
    ]],
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    headStyles: {
      fillColor: [55, 65, 81],
      textColor: 255,
      fontStyle: 'bold'
    },
    footStyles: {
      fillColor: [219, 234, 254],
      textColor: [30, 64, 175],
      fontStyle: 'bold'
    },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' }
    },
    didParseCell: function(data) {
      // Colorer les soldes
      if (data.column.index === 3 && data.section === 'body') {
        const text = data.cell.text[0];
        if (text && text.startsWith('+')) {
          data.cell.styles.textColor = [16, 185, 129];
        } else if (text && text.startsWith('-')) {
          data.cell.styles.textColor = [239, 68, 68];
        }
      }
      // Griser les lignes sans données
      if (data.section === 'body' && data.cell.text[0] === '—') {
        data.cell.styles.textColor = [156, 163, 175];
      }
    },
    margin: { left: 14, right: 14 }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // --- STATISTIQUES ---
  if (stats) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Statistiques', 14, yPos);
    
    yPos += 8;
    
    const statsData = [
      ['Moyenne mensuelle des recettes', formatMontant(stats.moyenneRecettes)],
      ['Meilleur mois (CA)', stats.meilleurMois ? `${stats.meilleurMois.mois_nom} (${formatMontant(stats.meilleurMois.total_recettes)})` : '—'],
      ['Mois le plus dépensier', stats.moisPlusDepensier ? `${stats.moisPlusDepensier.mois_nom} (${formatMontant(stats.moisPlusDepensier.total_depenses)})` : '—'],
      ['Taux de marge', `${stats.tauxMarge.toFixed(1)} %`]
    ];
    
    autoTable(doc, {
      startY: yPos,
      body: statsData,
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { halign: 'right' }
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      margin: { left: 14, right: 14 }
    });
  }
  
  // --- PIED DE PAGE ---
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'Document conforme à l\'article 99 du Code Général des Impôts',
    pageWidth / 2,
    pageHeight - 15,
    { align: 'center' }
  );
  doc.text(
    `Page 1/1 - Généré le ${formatDateTime()}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );
  
  // Ajouter le filigrane si plan gratuit
  if (hasWatermark) {
    addWatermark(doc);
  }
  
  // Télécharger le PDF
  const fileName = `Recapitulatif-${annee}.pdf`;
  doc.save(fileName);
  
  return fileName;
};


/**
 * Export Facture/Devis en PDF avec logo
 */
export const exportFacturePDF = async (document, type, entreprise, client, logoUrl = null, hasWatermark = false) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  let yPos = 15;
  
  // --- EN-TÊTE ---
  // Logo (si disponible)
  if (logoUrl) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = logoUrl;
      });
      doc.addImage(img, 'PNG', 14, yPos, 30, 30);
    } catch (e) {
      console.log('Logo non chargé, continuation sans logo');
    }
  }
  
  // Infos entreprise (à droite du logo)
  const entrepriseX = logoUrl ? 50 : 14;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 64, 175);
  doc.text(entreprise?.nom_entreprise || '5minCompta', entrepriseX, yPos + 5);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  if (entreprise?.siret) {
    doc.text(`SIRET: ${entreprise.siret}`, entrepriseX, yPos + 12);
  }
  if (entreprise?.adresse) {
    doc.text(entreprise.adresse, entrepriseX, yPos + 17);
  }
  
  // Titre du document (à droite)
  const isFacture = type === 'facture';
  const titre = isFacture ? 'FACTURE' : 'DEVIS';
  const couleur = isFacture ? [16, 185, 129] : [59, 130, 246]; // emerald / blue
  
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...couleur);
  doc.text(titre, pageWidth - 14, yPos + 10, { align: 'right' });
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);
  const numero = document.facture_number || document.devis_number || document.numero || '-';
  doc.text(`N° ${numero}`, pageWidth - 14, yPos + 18, { align: 'right' });
  
  const dateDoc = document.date || document.date_creation;
  doc.text(`Date: ${formatDate(dateDoc)}`, pageWidth - 14, yPos + 24, { align: 'right' });
  
  if (document.date_echeance) {
    doc.text(`Échéance: ${formatDate(document.date_echeance)}`, pageWidth - 14, yPos + 30, { align: 'right' });
  }
  
  yPos = 55;
  
  // --- CLIENT ---
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(pageWidth - 90, yPos, 76, 35, 3, 3, 'F');
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 100, 100);
  doc.text('DESTINATAIRE', pageWidth - 86, yPos + 6);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text(client?.name || client?.nom || 'Client', pageWidth - 86, yPos + 14);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(70, 70, 70);
  if (client?.address || client?.adresse) {
    doc.text(client.address || client.adresse, pageWidth - 86, yPos + 21);
  }
  if (client?.siret) {
    doc.text(`SIRET: ${client.siret}`, pageWidth - 86, yPos + 28);
  }
  
  yPos = 100;
  
  // --- LIGNES ---
  const lignes = document.lignes || document.items || [];
  
  if (lignes.length > 0) {
    const tableData = lignes.map(l => [
      l.description || l.designation || '-',
      l.quantite || l.quantity || 1,
      formatMontant(l.prix_unitaire || l.unit_price || 0),
      formatMontant((l.quantite || l.quantity || 1) * (l.prix_unitaire || l.unit_price || 0))
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Description', 'Qté', 'Prix unitaire HT', 'Total HT']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      headStyles: {
        fillColor: couleur,
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { halign: 'center', cellWidth: 25 },
        2: { halign: 'right', cellWidth: 40 },
        3: { halign: 'right', cellWidth: 40, fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // --- TOTAUX ---
  const totalHT = document.montant_ht || document.total_ht || document.montant || 0;
  const totalTVA = document.montant_tva || document.total_tva || 0;
  const totalTTC = document.montant_ttc || document.total_ttc || totalHT + totalTVA;
  
  // Box des totaux à droite
  const totauxX = pageWidth - 90;
  
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(totauxX, yPos, 76, 40, 3, 3, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(70, 70, 70);
  doc.text('Total HT', totauxX + 5, yPos + 10);
  doc.text(formatMontant(totalHT), totauxX + 71, yPos + 10, { align: 'right' });
  
  doc.text('TVA', totauxX + 5, yPos + 18);
  doc.text(formatMontant(totalTVA), totauxX + 71, yPos + 18, { align: 'right' });
  
  // Ligne de séparation
  doc.setDrawColor(200, 200, 200);
  doc.line(totauxX + 5, yPos + 23, totauxX + 71, yPos + 23);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...couleur);
  doc.text('Total TTC', totauxX + 5, yPos + 33);
  doc.text(formatMontant(totalTTC), totauxX + 71, yPos + 33, { align: 'right' });
  
  yPos += 55;
  
  // --- MENTIONS LÉGALES ---
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  
  const mentions = [
    'TVA non applicable, article 293 B du CGI (auto-entrepreneur)',
  ];
  
  if (isFacture) {
    mentions.push('En cas de retard de paiement, une indemnité forfaitaire de 40€ pour frais de recouvrement sera appliquée.');
  } else {
    const validite = document.validite || 30;
    mentions.push(`Ce devis est valable ${validite} jours à compter de sa date d'émission.`);
  }
  
  mentions.forEach((m, i) => {
    doc.text(m, 14, yPos + (i * 5));
  });
  
  // --- PIED DE PAGE ---
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Généré par 5minCompta', pageWidth / 2, pageHeight - 10, { align: 'center' });
  
  // Filigrane si plan gratuit
  if (hasWatermark) {
    addWatermark(doc);
  }
  
  // Télécharger
  const fileName = `${titre}-${numero}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

